import { useState, useRef } from 'react';
import ImageUploadService from '../../services/imageUploadService';
import toast from '../../utils/toast';

const imageUploadService = ImageUploadService.getInstance();

interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: 'treatment' | 'streak' | 'level' | 'quiz' | 'general';
  tier: 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond';
  criteria: {
    type: string;
    count?: number;
    days?: number;
    level?: number;
  };
  points_reward: number;
  color: string;
  active: boolean;
}

interface BadgeManagerProps {
  badges: Badge[];
  onBadgeCreated: (badge: Badge) => void;
  onBadgeUpdated: (badge: Badge) => void;
  onBadgeDeleted: (id: string) => void;
}

const getAuthHeader = () => {
  const tokensStr = localStorage.getItem('supabase.auth.token');
  if (!tokensStr) return null;
  
  try {
    const tokens = JSON.parse(tokensStr);
    return {
      'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${tokens.access_token}`,
      'Content-Type': 'application/json',
      'Prefer': 'return=representation'
    };
  } catch {
    return null;
  }
};

const tierColors = {
  bronze: { bg: 'from-amber-700 to-amber-900', text: 'text-amber-700', border: 'border-amber-300' },
  silver: { bg: 'from-gray-300 to-gray-500', text: 'text-gray-600', border: 'border-gray-300' },
  gold: { bg: 'from-yellow-400 to-yellow-600', text: 'text-yellow-600', border: 'border-yellow-300' },
  platinum: { bg: 'from-gray-200 to-gray-400', text: 'text-gray-500', border: 'border-gray-300' },
  diamond: { bg: 'from-cyan-300 to-blue-500', text: 'text-cyan-600', border: 'border-cyan-300' }
};

export default function BadgeManager({ badges, onBadgeCreated, onBadgeUpdated, onBadgeDeleted }: BadgeManagerProps) {
  const [showModal, setShowModal] = useState(false);
  const [editingBadge, setEditingBadge] = useState<Badge | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    category: 'general' as Badge['category'],
    tier: 'bronze' as Badge['tier'],
    criteriaType: 'first_dose',
    criteriaCount: 1,
    criteriaDays: 0,
    criteriaLevel: 0,
    points_reward: 10,
    color: '#CD7F32',
    active: true
  });

  const handleOpenModal = (badge?: Badge) => {
    if (badge) {
      setEditingBadge(badge);
      setFormData({
        name: badge.name,
        description: badge.description,
        icon: badge.icon,
        category: badge.category,
        tier: badge.tier,
        criteriaType: badge.criteria.type,
        criteriaCount: badge.criteria.count || 1,
        criteriaDays: badge.criteria.days || 0,
        criteriaLevel: badge.criteria.level || 0,
        points_reward: badge.points_reward,
        color: badge.color,
        active: badge.active
      });
    } else {
      setEditingBadge(null);
      setFormData({
        name: '',
        description: '',
        icon: '',
        category: 'general',
        tier: 'bronze',
        criteriaType: 'first_dose',
        criteriaCount: 1,
        criteriaDays: 0,
        criteriaLevel: 0,
        points_reward: 10,
        color: '#CD7F32',
        active: true
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const authHeader = getAuthHeader();
      if (!authHeader) {
        toast.error('Erro de autenticação');
        return;
      }

      const criteria: any = { type: formData.criteriaType };
      if (formData.criteriaCount > 0) criteria.count = formData.criteriaCount;
      if (formData.criteriaDays > 0) criteria.days = formData.criteriaDays;
      if (formData.criteriaLevel > 0) criteria.level = formData.criteriaLevel;

      const badgeData = {
        name: formData.name,
        description: formData.description,
        icon: formData.icon || '🏆', // Valor padrão se vazio
        category: formData.category,
        tier: formData.tier,
        criteria,
        points_reward: formData.points_reward,
        color: formData.color,
        active: formData.active
      };

      console.log('📤 [BadgeManager] Enviando dados:', badgeData);

      const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/badges${editingBadge ? `?id=eq.${editingBadge.id}` : ''}`;
      const method = editingBadge ? 'PATCH' : 'POST';

      const response = await fetch(url, {
        method,
        headers: authHeader,
        body: JSON.stringify(badgeData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [BadgeManager] Erro na resposta:', response.status, errorText);
        throw new Error(`Erro ao salvar badge: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      const savedBadge = Array.isArray(result) ? result[0] : result;

      if (editingBadge) {
        onBadgeUpdated({ ...savedBadge, id: savedBadge.id.toString() });
      } else {
        onBadgeCreated({ ...savedBadge, id: savedBadge.id.toString() });
      }

      setShowModal(false);
    } catch (error) {
      console.error('Erro ao salvar badge:', error);
      toast.error('Erro ao salvar badge');
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      event.target.value = '';
      return;
    }

    const validation = imageUploadService.validateImageFile(file);
    if (!validation.valid) {
      toast.warning(validation.error || 'Arquivo de imagem inválido');
      event.target.value = '';
      return;
    }

    try {
      const badgeName = formData.name;
      if (!badgeName) {
        toast.warning('Por favor, preencha o nome do badge antes de fazer upload da imagem');
        event.target.value = '';
        return;
      }

      // Preview local
      const previewUrl = URL.createObjectURL(file);
      setFormData(prev => ({ ...prev, icon: previewUrl }));

      // Upload real
      console.log('📤 [BadgeManager] Fazendo upload da imagem...');
      const uploadedUrl = await imageUploadService.uploadBadgeImage(file, badgeName);
      console.log('✅ [BadgeManager] Upload concluído:', uploadedUrl);
      setFormData(prev => ({ ...prev, icon: uploadedUrl }));
    } catch (error: any) {
      console.error('❌ [BadgeManager] Erro no upload:', error);
      toast.error(`Erro ao fazer upload da imagem: ${error.message}`);
    } finally {
      event.target.value = '';
    }
  };

  const openFileSelector = () => {
    fileInputRef.current?.click();
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja deletar este badge?')) return;

    try {
      const authHeader = getAuthHeader();
      if (!authHeader) return;

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/rest/v1/badges?id=eq.${id}`, {
        method: 'DELETE',
        headers: authHeader
      });

      if (!response.ok) {
        throw new Error('Erro ao deletar badge');
      }

      onBadgeDeleted(id);
    } catch (error) {
      console.error('Erro ao deletar badge:', error);
      toast.error('Erro ao deletar badge');
    }
  };

  return (
    <div className="bg-white rounded-3xl shadow-xl">
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">Badges / Conquistas</h3>
          <button
            onClick={() => handleOpenModal()}
            className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center hover:bg-purple-200 transition-colors"
          >
            <i className="ri-add-line text-purple-600"></i>
          </button>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {badges.map((badge) => (
            <div
              key={badge.id}
              className={`border-2 ${tierColors[badge.tier].border} rounded-2xl p-4 ${badge.active ? 'bg-white' : 'bg-gray-50 opacity-60'}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${tierColors[badge.tier].bg} flex items-center justify-center overflow-hidden`}>
                    {badge.icon ? (
                      <img src={badge.icon} alt={badge.name} className="w-full h-full object-cover" />
                    ) : (
                      <i className="ri-trophy-line text-white text-2xl"></i>
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">{badge.name}</h4>
                    <span className={`text-xs font-medium ${tierColors[badge.tier].text}`}>
                      {badge.tier.toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleOpenModal(badge)}
                    className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center hover:bg-blue-200"
                  >
                    <i className="ri-edit-line text-blue-600 text-sm"></i>
                  </button>
                  <button
                    onClick={() => handleDelete(badge.id)}
                    className="w-7 h-7 bg-red-100 rounded-lg flex items-center justify-center hover:bg-red-200"
                  >
                    <i className="ri-delete-bin-line text-red-600 text-sm"></i>
                  </button>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-3">{badge.description}</p>

              <div className="flex items-center justify-between text-xs">
                <span className="px-2 py-1 bg-gray-100 rounded-lg text-gray-600">
                  {badge.category}
                </span>
                <span className="font-semibold text-green-600">+{badge.points_reward} pts</span>
              </div>
            </div>
          ))}
        </div>

        {badges.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <i className="ri-trophy-line text-6xl mb-4"></i>
            <p>Nenhum badge cadastrado</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-xl font-bold text-gray-800">
                {editingBadge ? 'Editar Badge' : 'Novo Badge'}
              </h3>
            </div>

            <div className="p-6 space-y-4">
              {/* Nome */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nome</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Ex: Primeira Homeopatia"
                />
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows={3}
                  placeholder="Descrição do badge"
                />
              </div>

              {/* Imagem do Badge */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Imagem do Badge *</label>

                {/* Preview da imagem */}
                <div className="mb-4">
                  <div className="w-24 h-24 rounded-2xl overflow-hidden bg-gray-100 mx-auto flex items-center justify-center">
                    {formData.icon ? (
                      <img src={formData.icon} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <i className="ri-trophy-line text-gray-400 text-3xl"></i>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 text-center mt-2">Preview da imagem</p>
                </div>

                {/* Input de arquivo oculto */}
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />

                {/* Botões de ação */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <button
                    type="button"
                    onClick={openFileSelector}
                    className="py-2 px-3 bg-purple-100 text-purple-600 rounded-xl text-sm font-medium hover:bg-purple-200 transition-colors"
                  >
                    <i className="ri-upload-2-line mr-1"></i> Selecionar Imagem
                  </button>

                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, icon: '' })}
                    className="py-2 px-3 bg-red-100 text-red-600 rounded-xl text-sm font-medium hover:bg-red-200 transition-colors"
                  >
                    <i className="ri-delete-bin-line mr-1"></i> Limpar
                  </button>
                </div>

                {/* Campo manual URL */}
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-2">Ou insira URL personalizada:</p>
                  <input
                    type="url"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    placeholder="https://exemplo.com/imagem.jpg"
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">URL de imagem ou deixe em branco para usar ícone padrão</p>
                </div>
              </div>

              {/* Tier */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tier</label>
                <select
                  value={formData.tier}
                  onChange={(e) => setFormData({ ...formData, tier: e.target.value as Badge['tier'] })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="bronze">Bronze</option>
                  <option value="silver">Prata</option>
                  <option value="gold">Ouro</option>
                  <option value="platinum">Platina</option>
                  <option value="diamond">Diamante</option>
                </select>
              </div>

              {/* Categoria e Pontos */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Categoria</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value as Badge['category'] })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="general">Geral</option>
                    <option value="treatment">Tratamento</option>
                    <option value="streak">Sequência</option>
                    <option value="level">Nível</option>
                    <option value="quiz">Quiz</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pontos de Recompensa</label>
                  <input
                    type="number"
                    value={formData.points_reward}
                    onChange={(e) => setFormData({ ...formData, points_reward: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Critérios */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Critério</label>
                <select
                  value={formData.criteriaType}
                  onChange={(e) => setFormData({ ...formData, criteriaType: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="first_dose">Primeira Dose</option>
                  <option value="first_treatment">Primeiro Tratamento</option>
                  <option value="treatment_streak">Sequência de Tratamento</option>
                  <option value="illness_streak">Sequência sem Doenças</option>
                  <option value="level_reached">Nível Alcançado</option>
                  <option value="quiz_completed">Quiz Completado</option>
                  <option value="days_active">Dias Ativos</option>
                  <option value="doses_on_time">Doses no Horário</option>
                  <option value="treatment_completed">Tratamento Concluído</option>
                </select>
              </div>

              {/* Valores dos critérios */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Count</label>
                  <input
                    type="number"
                    value={formData.criteriaCount}
                    onChange={(e) => setFormData({ ...formData, criteriaCount: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Days</label>
                  <input
                    type="number"
                    value={formData.criteriaDays}
                    onChange={(e) => setFormData({ ...formData, criteriaDays: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Level</label>
                  <input
                    type="number"
                    value={formData.criteriaLevel}
                    onChange={(e) => setFormData({ ...formData, criteriaLevel: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Cor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cor (Hex)</label>
                <input
                  type="text"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="#CD7F32"
                />
              </div>

              {/* Ativo */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                />
                <label className="text-sm font-medium text-gray-700">Badge Ativo</label>
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                className="flex-1 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700"
              >
                {editingBadge ? 'Atualizar' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
